/**
 * EVENTSFLO-I18N-PHASE-2F-DYNAMIC-CONTENT-CACHE-V1
 *
 * SuperAdmin-only catalog/cache preparation for approved drafts.
 *
 * Safety:
 *   - Returns null for non-SuperAdmin.
 *   - Production English UI is unchanged by this card.
 *   - There is intentionally no "Enable translations" / runtime
 *     language toggle. `production_activation` is always false.
 *   - Apply button is gated by the EXACT phrase
 *     `BUILD ADMIN I18N CATALOG CACHE`. Anything else keeps the
 *     button disabled.
 *   - No API key, provider secret, MONGO_URL, or Authorization
 *     header can appear in the DOM.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  Download,
  FileText,
  Info,
  Play,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

const ENDPOINT = "/api/admin/i18n/catalog";
const APPLY_PHRASE = "BUILD ADMIN I18N CATALOG CACHE";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

async function authedFetch(path, token, options = {}) {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  let body = null;
  try { body = await r.json(); } catch { body = null; }
  if (!r.ok) {
    const detail = body?.detail || body || { error: `HTTP ${r.status}` };
    const msg =
      typeof detail === "string"
        ? detail
        : detail.message || detail.error || `HTTP ${r.status}`;
    const err = new Error(msg);
    err.status = r.status;
    throw err;
  }
  return body;
}

export default function I18nCatalogCacheCard() {
  const { user, token } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [cache, setCache] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheError, setCacheError] = useState(null);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [dryRunResult, setDryRunResult] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [confirmPhrase, setConfirmPhrase] = useState("");

  const loadCache = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setCacheLoading(true);
    setCacheError(null);
    try {
      const body = await authedFetch(`${ENDPOINT}/cache`, token);
      setCache(body);
    } catch (e) {
      setCacheError(e.message || "Failed to load catalog cache");
    } finally {
      setCacheLoading(false);
    }
  }, [isSuperAdmin, token]);

  useEffect(() => { loadCache(); }, [loadCache]);

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const body = await authedFetch(`${ENDPOINT}/preview`, token);
      setPreview(body);
    } catch (e) {
      toast.error(e.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const runDryBuild = async () => {
    setActionLoading(true);
    try {
      const body = await authedFetch(`${ENDPOINT}/build-cache`, token, {
        method: "POST",
        body: JSON.stringify({ dry_run: true }),
      });
      setDryRunResult(body);
      toast.success(
        `Dry run complete — ${body.total_cached} entries ready (no cache written)`,
      );
    } catch (e) {
      toast.error(e.message || "Dry run failed");
    } finally {
      setActionLoading(false);
    }
  };

  const applyBuild = async () => {
    if (confirmPhrase !== APPLY_PHRASE) {
      toast.error("Confirmation phrase does not match.");
      return;
    }
    setActionLoading(true);
    try {
      const body = await authedFetch(`${ENDPOINT}/build-cache`, token, {
        method: "POST",
        body: JSON.stringify({
          dry_run: false,
          confirm: APPLY_PHRASE,
        }),
      });
      toast.success(
        `Cache built — ${body.total_cached} entries (production_activation=false)`,
      );
      setConfirmPhrase("");
      loadCache();
    } catch (e) {
      toast.error(e.message || "Apply failed");
    } finally {
      setActionLoading(false);
    }
  };

  const exportApproved = async () => {
    setActionLoading(true);
    try {
      const body = await authedFetch(`${ENDPOINT}/export-approved`, token);
      const blob = new Blob([JSON.stringify(body, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      a.href = url;
      a.download = `eventsflo-i18n-approved-drafts-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${body.total_entries} approved entries`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setActionLoading(false);
    }
  };

  const applyEnabled = useMemo(
    () => confirmPhrase === APPLY_PHRASE && !actionLoading,
    [confirmPhrase, actionLoading],
  );

  if (!isSuperAdmin) return null;

  return (
    <Card data-testid="i18n-catalog-cache-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="w-4 h-4" /> Admin i18n catalog cache
        </CardTitle>
        <p className="text-xs text-zinc-400 mt-1">
          SuperAdmin-only. This cache prepares approved translations
          for future use. It <strong className="text-zinc-300">does
          not</strong> change the live English UI.
        </p>
      </CardHeader>
      <CardContent>
        {/* Production-unchanged banner */}
        <div
          className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200 flex items-start gap-2"
          data-testid="i18n-catalog-production-unchanged-note"
        >
          <Info className="w-3.5 h-3.5 mt-0.5" />
          <span>
            production_activation: <strong>false</strong>. English
            remains the default language. Approved translations are
            <strong> not </strong>
            wired into the runtime translation lookup.
          </span>
        </div>

        {/* Cache metadata */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-catalog-cache-metadata-section"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Database className="w-3.5 h-3.5" /> Current cache
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadCache}
              disabled={cacheLoading}
              data-testid="i18n-catalog-cache-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
          {cacheError ? (
            <div className="text-xs text-rose-300" data-testid="i18n-catalog-cache-error">
              {cacheError}
            </div>
          ) : !cache ? (
            <div className="text-xs text-zinc-500">Loading…</div>
          ) : !cache.exists ? (
            <div
              className="text-xs text-zinc-500"
              data-testid="i18n-catalog-cache-empty"
            >
              No catalog cache has been built yet. Run a dry-run, then
              type the exact confirmation phrase to apply.
            </div>
          ) : (
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm"
              data-testid="i18n-catalog-cache-grid"
            >
              <div>
                <div className="text-zinc-400 text-xs">cache id</div>
                <div
                  className="font-mono text-xs text-zinc-300"
                  data-testid="i18n-catalog-cache-id"
                >
                  {String(cache.cache_id).slice(0, 8)}…
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">built at</div>
                <div className="text-zinc-300" data-testid="i18n-catalog-cache-built-at">
                  {fmt(cache.built_at)}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">checksum</div>
                <div
                  className="font-mono text-xs text-zinc-300"
                  data-testid="i18n-catalog-cache-checksum"
                >
                  {cache.checksum ? `${cache.checksum.slice(0, 12)}…` : "—"}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">entries</div>
                <div className="text-zinc-300" data-testid="i18n-catalog-cache-total-entries">
                  {cache.total_entries}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">namespaces</div>
                <div className="text-zinc-300 font-mono text-xs">
                  {(cache.namespaces || []).join(", ") || "—"}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">languages</div>
                <div className="text-zinc-300 font-mono text-xs">
                  {(cache.languages || []).join(", ") || "—"}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">status</div>
                <div>
                  <Badge
                    className="bg-zinc-500/15 text-zinc-300 border border-zinc-500/30"
                    data-testid="i18n-catalog-cache-status-badge"
                  >
                    {cache.status || "—"}
                  </Badge>
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">production activation</div>
                <div className="text-zinc-300">
                  <Badge
                    className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                    data-testid="i18n-catalog-production-activation-badge"
                  >
                    No
                  </Badge>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Preview */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-catalog-preview-section"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" /> Preview approved catalog
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadPreview}
              disabled={previewLoading}
              data-testid="i18n-catalog-preview-btn"
            >
              <Play className="w-3.5 h-3.5 mr-1" /> Preview
            </Button>
          </div>
          {preview ? (
            <div className="text-xs space-y-2" data-testid="i18n-catalog-preview-result">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <div className="text-zinc-400">approved</div>
                  <div className="text-zinc-300" data-testid="i18n-catalog-preview-total-approved">
                    {preview.total_approved}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">cached</div>
                  <div className="text-zinc-300">{preview.total_cached}</div>
                </div>
                <div>
                  <div className="text-zinc-400">skipped</div>
                  <div className="text-zinc-300">{preview.total_skipped}</div>
                </div>
                <div>
                  <div className="text-zinc-400">checksum</div>
                  <div
                    className="font-mono text-zinc-300"
                    data-testid="i18n-catalog-preview-checksum"
                  >
                    {preview.checksum ? `${preview.checksum.slice(0, 12)}…` : "—"}
                  </div>
                </div>
              </div>
              <pre
                className="bg-zinc-900/60 border border-zinc-700/40 rounded p-2 overflow-auto max-h-60 text-zinc-300"
                data-testid="i18n-catalog-preview-pre"
              >
{JSON.stringify(preview.catalog_preview, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="text-xs text-zinc-500">
              Click Preview to inspect the approved catalog shape
              (no mutation).
            </div>
          )}
        </div>

        {/* Build cache */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-catalog-build-section"
        >
          <div className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
            <Database className="w-3.5 h-3.5" /> Build catalog cache
          </div>
          <p className="text-xs text-zinc-500 mb-2">
            Dry-run is mutation-free. Apply writes a cache record but
            still <strong>does not</strong> activate runtime
            translations.
          </p>
          <div className="flex flex-wrap gap-2 mb-2">
            <Button
              size="sm"
              variant="outline"
              onClick={runDryBuild}
              disabled={actionLoading}
              data-testid="i18n-catalog-build-dry-run-btn"
            >
              <Play className="w-3.5 h-3.5 mr-1" /> Dry-run
            </Button>
          </div>
          {dryRunResult ? (
            <div
              className="rounded border border-zinc-700/40 bg-zinc-900/40 p-2 text-xs grid grid-cols-2 md:grid-cols-4 gap-2 mb-2"
              data-testid="i18n-catalog-build-dry-run-result"
            >
              <div>
                <div className="text-zinc-400">dry_run</div>
                <div className="text-zinc-300">{String(dryRunResult.dry_run)}</div>
              </div>
              <div>
                <div className="text-zinc-400">would cache</div>
                <div className="text-zinc-300">{dryRunResult.total_cached}</div>
              </div>
              <div>
                <div className="text-zinc-400">checksum</div>
                <div className="font-mono text-zinc-300">
                  {dryRunResult.checksum ? `${dryRunResult.checksum.slice(0, 12)}…` : "—"}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">activation</div>
                <div className="text-zinc-300">
                  {String(dryRunResult.production_activation)}
                </div>
              </div>
            </div>
          ) : null}

          <div className="mt-2">
            <label
              htmlFor="i18n-catalog-confirm-input"
              className="text-xs text-zinc-400 block mb-1"
            >
              Type exactly to enable Apply:
              <span className="font-mono text-amber-300 ml-1">
                {APPLY_PHRASE}
              </span>
            </label>
            <div className="flex flex-wrap gap-2 items-center">
              <Input
                id="i18n-catalog-confirm-input"
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                placeholder={APPLY_PHRASE}
                className="max-w-md font-mono text-xs"
                data-testid="i18n-catalog-confirm-input"
              />
              <Button
                onClick={applyBuild}
                disabled={!applyEnabled}
                data-testid="i18n-catalog-build-apply-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Apply build
              </Button>
              {!applyEnabled ? (
                <div
                  className="text-xs text-zinc-400 flex items-center gap-1"
                  data-testid="i18n-catalog-confirm-helper"
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                  Exact phrase required.
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Export approved */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-catalog-export-section"
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Download className="w-3.5 h-3.5" /> Export approved drafts
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Approved drafts only. No provider secrets are
                included. For review / diff / audit. No production
                change.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={exportApproved}
              disabled={actionLoading}
              data-testid="i18n-catalog-export-approved-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> Export JSON
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
